import axios, {
  AxiosInstance,
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { showNotify } from "vant";
import {
  getAuthToken,
  handleUnauthorized,
  redirectToPlatformNotice,
  SERVER_API_PATH,
} from "@/utils/wechat";
// 数据返回的接口
// 定义请求响应参数，不含data
interface Result {
  code: number;
  msg: string;
}

// 请求响应参数，包含data
interface ResultData<T = any> extends Result {
  data?: T;
  rows: [];
  total: number;
}
const URL = process.env.VUE_APP_BASE_API;
enum RequestEnums {
  TIMEOUT = 20000,
  OVERDUE = 401, // 认证失败
  FAIL = 500, // 请求失败
  SUCCESS = 200, // 请求成功
  // 非企业微信环境（微信等打开分享链接）：后端任意业务接口返回该 code 时，
  // 前端不弹错误提示，统一跳转到平台提示页（见下方响应拦截器）
  PLATFORM = 406,
}
const config = {
  // 默认地址
  baseURL: URL as string,
  // 设置超时时间
  timeout: RequestEnums.TIMEOUT as number,
  // 跨域时候允许携带凭证
  withCredentials: true,
};

class RequestHttp {
  // 定义成员变量并指定类型
  service: AxiosInstance;
  public constructor(config: AxiosRequestConfig) {
    // 实例化axios
    this.service = axios.create(config);

    /**
     * 请求拦截器
     * 客户端发送请求 -> [请求拦截器] -> 服务器
     * token校验(JWT) : 接受服务器返回的token,存储到vuex/pinia/本地储存当中
     */
    this.service.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        // 是否需要设置 token
        const isToken = (config.headers || {}).isToken === false;
        const token = getAuthToken();
        if (token && !isToken) {
          config.headers.setAuthorization("Bearer " + token);
        }
        config.headers.setContentType(
          config.headers["Content-Type"] || "application/json;charset=utf-8"
        );
        return {
          ...config,
        };
      },
      (error: AxiosError) => {
        return Promise.reject(error);
      }
    );

    /**
     * 响应拦截器
     * 服务器换返回信息 -> [拦截统一处理] -> 客户端JS获取到信息
     */
    this.service.interceptors.response.use(
      (response: AxiosResponse) => {
        const { data, config } = response; // 解构
        if (data.code === RequestEnums.OVERDUE) {
          // 登录信息失效：清除本地凭证并尝试跳转授权页重新获取
          handleUnauthorized(config?.url);
          return Promise.reject(data);
        }
        if (data.code === RequestEnums.PLATFORM) {
          // 微信等非企业微信环境打开分享链接：后端返回该业务 code，
          // 不弹错误提示，统一跳转到平台提示页（axios 层统一处理，覆盖所有接口）
          redirectToPlatformNotice();
          return Promise.reject(data);
        }
        // 全局错误信息拦截（防止下载文件得时候返回数据流，没有code，直接报错）
        if (data.code && data.code !== RequestEnums.SUCCESS) {
          showNotify({ message: data.msg }); // 此处也可以使用组件提示报错信息
          return Promise.reject(data);
        }
        return data;
      },
      (error: AxiosError) => {
        const { response } = error;
        if (response) {
          if (response.status === RequestEnums.OVERDUE) {
            // HTTP 状态 401 与业务 code 401 同样处理：
            // 之前只弹提示不恢复登录，导致 token 被清/残留后没有任何路径重新授权
            handleUnauthorized(response.config?.url);
          } else {
            this.handleCode(response.status);
          }
        }
        if (!window.navigator.onLine) {
          showNotify({ message: "网络连接失败" });
          // 可以跳转到错误页面，也可以不做操作
          // return router.replace({
          //   path: "/404"
          // });
        }
        // 必须继续抛出，否则调用方 await 拿到 undefined，引发 res.data 二次报错
        return Promise.reject(error);
      }
    );
  }
  handleCode(code: number): void {
    switch (code) {
      case 401:
        showNotify({ message: "登录失败，请重新登录" });
        break;
      default:
        showNotify({ message: "请求失败" });
        break;
    }
  }

  // 常用方法封装
  get<T>(url: string, params?: unknown): Promise<ResultData<T>> {
    return this.service.get(url, { params });
  }
  post<T>(
    url: string,
    params?: unknown,
    config?: AxiosRequestConfig
  ): Promise<ResultData<T>> {
    return this.service.post(url, params, config);
  }
  put<T>(url: string, params?: unknown): Promise<ResultData<T>> {
    return this.service.put(url, params);
  }
  delete<T>(url: string, params?: unknown): Promise<ResultData<T>> {
    return this.service.delete(url, { params });
  }
  http<T>(config: AxiosRequestConfig): Promise<ResultData<T>> {
    return this.service(config);
  }
  /**
   * 走认证服务（VUE_APP_SERVER_API 前缀）的请求
   * 与 http 共用同一拦截器（token、401 处理），仅 baseURL 不同；
   * 未配置 VUE_APP_SERVER_API 时退化为默认 baseURL，避免误配导致请求 404
   */
  server<T>(config: AxiosRequestConfig): Promise<ResultData<T>> {
    return this.service({
      ...config,
      ...(SERVER_API_PATH ? { baseURL: SERVER_API_PATH } : {}),
    });
  }
}

// 导出一个实例对象
export default new RequestHttp(config);
